import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fabric } from 'fabric';

import { artboardFromImage } from './artboard';
import { colorForRole } from './colors';
import { isImageObject, isTextObject, newLayerId } from './fabric-setup';
import { loadFont, loadFontsFor, remeasureText } from './fonts';
import { iconSvg } from './icons';
import {
    applyMask,
    Box,
    containFit,
    coverFit,
    fitTextWidth,
    groupColor,
    loadImageElement,
    makeImage,
    makeLogo,
    makeSvgGroup,
    naturalSize,
    recolorGroup,
    refreshMask,
    setLocked,
} from './objects';
import { createDivider, createScrim, createShape, DividerId, ScrimId, ShapeId } from './shapes';
import { attachSnapping } from './snapping';
import { relinkGraphics } from './linked-graphics';
import { findTemplate } from './templates';
import { buildTemplateScene, isTemplateId } from './template-scene';
import { buildTextPreset, TextPresetId } from './text-presets';
import type { ArtboardSize, BrandInfo, FontPair, MaskKind, Translate } from './types';

/**
 * The Design editor's controller: one Fabric canvas that fills the centre
 * pane, an artboard drawn inside it at its native pixel size, and every action
 * the panels can take.
 *
 * Coordinates are ARTBOARD pixels throughout (a 1080×1350 post has layers in
 * 0..1080 × 0..1350); zoom and pan are only the viewport transform. That keeps
 * a saved scene independent of the window it was edited in, and makes export a
 * plain 1:1 render of the artboard.
 *
 * Scenes saved by this editor carry `csScene: 2` plus the artboard size, and
 * include the picture being designed on as a locked bottom layer. Anything
 * older — scenes from the previous editor (background image + display-pixel
 * layers + authoredWidth) and the agent's scenes (layers at the source's full
 * size) — is upgraded on load.
 */

export interface EditorView { zoom: number; tx: number; ty: number }

export interface DesignEditorApi {
    ready: boolean;
    canvas: fabric.Canvas | null;
    artboard: ArtboardSize;
    view: EditorView;
    /** Bumped on every change; components read live object state during render. */
    version: number;
    canUndo: boolean;
    canRedo: boolean;
    cropping: boolean;
    transforming: boolean;
    spaceDown: boolean;
    selection: fabric.Object[];
    active: fabric.Object | null;

    undo: () => void;
    redo: () => void;
    commit: () => void;
    commitSoon: () => void;
    refresh: () => void;

    // Adding
    addObjects: (objs: fabric.Object[], opts?: { select?: boolean }) => void;
    addTextPreset: (id: TextPresetId) => Promise<void>;
    addTextBox: () => Promise<void>;
    addShape: (id: ShapeId) => void;
    addDivider: (id: DividerId) => void;
    addScrim: (id: ScrimId) => void;
    addIcon: (id: string) => Promise<void>;
    addImage: (url: string, opts?: { mask?: MaskKind; asFrame?: boolean }) => Promise<void>;
    addLogo: (url: string) => Promise<void>;

    // Selection-wide edits
    update: (patch: Record<string, any>, opts?: { history?: 'now' | 'soon' | 'none'; filter?: (o: fabric.Object) => boolean }) => void;
    forEachSelected: (fn: (o: fabric.Object) => void, history?: 'now' | 'soon') => void;
    setFont: (family: string) => Promise<void>;
    setTextStyle: (patch: { fontWeight?: number; fontStyle?: string }) => Promise<void>;
    setColor: (color: string) => void;
    setPlate: (on: boolean) => void;

    deleteSelection: () => void;
    duplicate: () => void;
    copy: () => void;
    paste: () => void;
    group: () => void;
    ungroup: () => void;
    selectAll: () => void;
    select: (objs: fabric.Object[] | fabric.Object | null) => void;
    align: (mode: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom') => void;
    distribute: (axis: 'h' | 'v') => void;
    order: (dir: 'front' | 'forward' | 'backward' | 'back') => void;
    reorder: (obj: fabric.Object, toIndex: number) => void;
    toggleLock: (obj?: fabric.Object) => void;
    toggleVisible: (obj: fabric.Object) => void;
    rename: (obj: fabric.Object, name: string) => void;
    nudge: (dx: number, dy: number) => void;
    replaceObject: (prev: fabric.Object, next: fabric.Object) => void;
    flip: (axis: 'h' | 'v') => void;
    setGeometry: (patch: { x?: number; y?: number; w?: number; h?: number; angle?: number }) => void;

    // Images
    replaceImage: (url: string, target?: fabric.Image) => Promise<void>;
    setMask: (kind: MaskKind, radius?: number) => void;
    setAsBackground: (target?: fabric.Image) => void;
    setBackgroundFromUrl: (url: string) => Promise<void>;
    fitToArtboard: (target?: fabric.Image, mode?: 'cover' | 'contain') => void;
    applyFilters: (patch: Partial<FilterState>) => void;
    startCrop: () => void;
    applyCrop: () => void;
    cancelCrop: () => void;
    setCropAspect: (ratio: number | null) => void;

    // Artboard & view
    setArtboard: (size: ArtboardSize) => void;
    setArtboardColor: (color: string) => void;
    zoomTo: (z: number) => void;
    zoomBy: (factor: number) => void;
    zoomFit: () => void;

    // Templates & brand
    applyTemplate: (id: string, opts?: { pair?: FontPair; useBrandFont?: boolean }) => Promise<void>;
    applyBrand: () => Promise<void>;
    templateImages: () => string[];

    // Output
    sceneJSON: () => any;
    exportBlob: (type?: 'image/png' | 'image/jpeg', quality?: number) => Promise<Blob>;
}

export interface FilterState { brightness: number; contrast: number; saturation: number; blur: number; grayscale: boolean }

interface Params {
    open: boolean;
    canvasEl: HTMLCanvasElement | null;
    paneEl: HTMLDivElement | null;
    backgroundUrl: string | null;
    initialFabricJson?: any;
    initialImageLayerUrls?: string[];
    brand: BrandInfo;
    pair: FontPair;
    projectImages: string[];
    t: Translate;
}

const MAX_HISTORY = 60;
const SELECTION_BLUE = '#3B82F6';

/** Selection chrome, applied to every object as it lands on the canvas. */
function decorate(o: fabric.Object): void {
    const any = o as any;
    if (!any.csId) any.csId = newLayerId();
    o.set({
        borderColor: SELECTION_BLUE,
        cornerColor: '#FFFFFF',
        cornerStrokeColor: SELECTION_BLUE,
        cornerStyle: 'circle',
        cornerSize: 10,
        transparentCorners: false,
        borderScaleFactor: 1.5,
        lockScalingFlip: true,
    } as any);
    if (isTextObject(o) && (any.bgPadX || any.bgPadY)) {
        o.set({ padding: Math.max(any.bgPadX || 0, any.bgPadY || 0) } as any);
    }
    // Pictures, logos and icons scale proportionally: side handles would only
    // distort them.
    if (o.type === 'image' || o.type === 'group') {
        o.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
    }
    if (any.csLocked) setLocked(o, true);
}

export function useDesignEditor(p: Params): DesignEditorApi {
    const { open, canvasEl, paneEl } = p;
    const canvasRef = useRef<fabric.Canvas | null>(null);
    const artRef = useRef<ArtboardSize>({ w: 1080, h: 1080 });
    const viewRef = useRef<EditorView>({ zoom: 1, tx: 0, ty: 0 });
    const fitModeRef = useRef(true);
    const paramsRef = useRef(p);
    paramsRef.current = p;

    const [ready, setReady] = useState(false);
    const [artboard, setArtboardState] = useState<ArtboardSize>({ w: 1080, h: 1080 });
    const [view, setView] = useState<EditorView>({ zoom: 1, tx: 0, ty: 0 });
    const [version, setVersion] = useState(0);
    const [counts, setCounts] = useState({ past: 0, future: 0 });
    const [cropping, setCropping] = useState(false);
    const [transforming, setTransforming] = useState(false);
    const [spaceDown, setSpaceDown] = useState(false);
    const spaceRef = useRef(false);
    /** The pictures the design opened with, in order — templates fill from these first. */
    const baseImagesRef = useRef<string[]>([]);

    const bump = useCallback(() => setVersion((v) => v + 1), []);

    // ─── History ─────────────────────────────────────────────────────
    const pastRef = useRef<string[]>([]);
    const futureRef = useRef<string[]>([]);
    const suspendRef = useRef(0);
    const soonTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const sceneJSON = useCallback((): any => {
        const c = canvasRef.current;
        if (!c) return null;
        const json: any = c.toJSON();
        delete json.clipPath;
        delete json.backgroundImage;
        json.objects = (json.objects || []).filter((o: any) => !o.csTemp);
        json.csScene = 2;
        json.artboardWidth = artRef.current.w;
        json.artboardHeight = artRef.current.h;
        // Same meaning as the previous editor's field: the width these
        // coordinates were authored at. The headless renderer and older
        // readers keep mapping scenes correctly from it.
        json.authoredWidth = artRef.current.w;
        return json;
    }, []);

    const syncCounts = useCallback(() => {
        setCounts({ past: pastRef.current.length, future: futureRef.current.length });
    }, []);

    const commit = useCallback(() => {
        if (suspendRef.current > 0 || !canvasRef.current) return;
        if (soonTimer.current) { clearTimeout(soonTimer.current); soonTimer.current = null; }
        const snap = JSON.stringify(sceneJSON());
        const past = pastRef.current;
        if (past[past.length - 1] === snap) return;
        past.push(snap);
        if (past.length > MAX_HISTORY) past.shift();
        futureRef.current = [];
        syncCounts();
    }, [sceneJSON, syncCounts]);

    const commitSoon = useCallback(() => {
        if (soonTimer.current) clearTimeout(soonTimer.current);
        soonTimer.current = setTimeout(() => { soonTimer.current = null; commit(); }, 380);
    }, [commit]);

    // ─── Canvas plumbing ─────────────────────────────────────────────
    const applyClip = useCallback(() => {
        const c = canvasRef.current;
        if (!c) return;
        const a = artRef.current;
        (c as any).clipPath = new fabric.Rect({ left: 0, top: 0, width: a.w, height: a.h, originX: 'left', originY: 'top' } as any);
    }, []);

    const publishView = useCallback(() => {
        const c = canvasRef.current;
        if (!c) return;
        const v = c.viewportTransform || [1, 0, 0, 1, 0, 0];
        viewRef.current = { zoom: v[0], tx: v[4], ty: v[5] };
        setView(viewRef.current);
        c.getObjects().forEach((o) => o.setCoords());
        c.getActiveObject()?.setCoords();
    }, []);

    const zoomFit = useCallback(() => {
        const c = canvasRef.current;
        if (!c) return;
        const pw = c.getWidth();
        const ph = c.getHeight();
        const a = artRef.current;
        const pad = Math.min(56, Math.max(24, Math.min(pw, ph) * 0.06));
        const z = Math.max(0.02, Math.min((pw - pad * 2) / a.w, (ph - pad * 2) / a.h));
        c.setViewportTransform([z, 0, 0, z, (pw - a.w * z) / 2, (ph - a.h * z) / 2]);
        fitModeRef.current = true;
        publishView();
        c.requestRenderAll();
    }, [publishView]);

    const zoomTo = useCallback((z: number, point?: { x: number; y: number }) => {
        const c = canvasRef.current;
        if (!c) return;
        const clamped = Math.max(0.05, Math.min(8, z));
        const pt = point || { x: c.getWidth() / 2, y: c.getHeight() / 2 };
        c.zoomToPoint(new fabric.Point(pt.x, pt.y), clamped);
        fitModeRef.current = false;
        publishView();
        c.requestRenderAll();
    }, [publishView]);

    const zoomBy = useCallback((factor: number) => {
        zoomTo((canvasRef.current?.getZoom() || 1) * factor);
    }, [zoomTo]);

    const setArtboardInternal = useCallback((size: ArtboardSize) => {
        artRef.current = { w: Math.round(size.w), h: Math.round(size.h) };
        setArtboardState(artRef.current);
        applyClip();
    }, [applyClip]);

    const isBackground = (o: fabric.Object) => (o as any).csRole === 'background';

    const fitBackgroundLayers = useCallback(() => {
        const c = canvasRef.current;
        if (!c) return;
        const a = artRef.current;
        c.getObjects().forEach((o) => {
            if (isBackground(o) && isImageObject(o)) coverFit(o, { x: 0, y: 0, w: a.w, h: a.h });
            else if (isBackground(o) && o.type === 'rect') o.set({ left: 0, top: 0, width: a.w, height: a.h, scaleX: 1, scaleY: 1 });
        });
    }, []);

    // ─── Restore (history + load) ────────────────────────────────────
    const loadScene = useCallback((json: any): Promise<void> => {
        const c = canvasRef.current;
        if (!c) return Promise.resolve();
        suspendRef.current += 1;
        return new Promise((resolve) => {
            c.loadFromJSON(json, () => {
                c.backgroundImage = undefined as any;
                c.getObjects().forEach(decorate);
                applyClip();
                c.requestRenderAll();
                suspendRef.current -= 1;
                resolve();
            });
        });
    }, [applyClip]);

    const restore = useCallback(async (snap: string) => {
        const c = canvasRef.current;
        if (!c) return;
        const state = JSON.parse(snap);
        c.discardActiveObject();
        setArtboardInternal({ w: state.artboardWidth || artRef.current.w, h: state.artboardHeight || artRef.current.h });
        await loadScene(state);
        if (fitModeRef.current) zoomFit();
        bump();
    }, [loadScene, setArtboardInternal, zoomFit, bump]);

    const undo = useCallback(() => {
        const past = pastRef.current;
        if (past.length < 2) return;
        futureRef.current.unshift(past.pop()!);
        syncCounts();
        void restore(past[past.length - 1]);
    }, [restore, syncCounts]);

    const redo = useCallback(() => {
        const next = futureRef.current.shift();
        if (!next) return;
        pastRef.current.push(next);
        syncCounts();
        void restore(next);
    }, [restore, syncCounts]);

    /** Run a multi-step change as ONE history entry. */
    const batch = useCallback(async (fn: () => void | Promise<void>) => {
        suspendRef.current += 1;
        try { await fn(); } finally { suspendRef.current -= 1; }
        commit();
        bump();
    }, [commit, bump]);

    // ─── Init ─────────────────────────────────────────────────────────
    const initKey = `${open}|${!!canvasEl}|${!!paneEl}|${p.backgroundUrl || ''}`;
    useEffect(() => {
        if (!open || !canvasEl || !paneEl) return undefined;
        let disposed = false;
        let raf = 0;
        let snapping: { detach: () => void } | null = null;
        let ro: ResizeObserver | null = null;

        const start = async () => {
            const box = paneEl.getBoundingClientRect();
            if (box.width < 100 || box.height < 100) {
                raf = requestAnimationFrame(() => { void start(); });
                return;
            }
            if (canvasRef.current) { canvasRef.current.dispose(); canvasRef.current = null; }
            const c = new fabric.Canvas(canvasEl, {
                width: Math.floor(box.width),
                height: Math.floor(box.height),
                preserveObjectStacking: true,
                controlsAboveOverlay: true,
                backgroundColor: '#FFFFFF',
                selection: true,
                selectionColor: 'rgba(59, 130, 246, 0.06)',
                selectionBorderColor: SELECTION_BLUE,
                selectionLineWidth: 1,
                fireMiddleClick: true,
                stopContextMenu: true,
                targetFindTolerance: 4,
            } as any);
            canvasRef.current = c;
            // Fabric types into a hidden <textarea> it appends to <body> — outside
            // the dialog, whose focus trap pulls focus straight back, so inline
            // editing selected the words and then ignored every key. Keep the
            // textarea inside the editor while it is open.
            (fabric.IText.prototype as any).hiddenTextareaContainer = paneEl;
            // The artboard is the only "paper": its colour fills the artboard
            // rect, not the whole pane, and the clip path hides overflow.
            (c as any)._renderBackground = function renderArtboard(ctx: CanvasRenderingContext2D) {
                const v = this.viewportTransform;
                const a = artRef.current;
                const fill = this.backgroundColor;
                if (!fill) return;
                ctx.save();
                ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
                ctx.fillStyle = fill.toLive ? fill.toLive(ctx, this) : fill;
                ctx.fillRect(0, 0, a.w, a.h);
                ctx.restore();
            };

            const { backgroundUrl, initialImageLayerUrls } = paramsRef.current;
            let json = paramsRef.current.initialFabricJson;
            // A plan's template design opened before it was ever rendered: lay
            // the template out now, the same way the headless renderer would.
            if (json?.csTemplate && isTemplateId(json.csTemplate.id) && backgroundUrl) {
                try {
                    const el = await loadImageElement(backgroundUrl);
                    json = await buildTemplateScene({
                        request: json.csTemplate,
                        imageUrl: backgroundUrl,
                        imageWidth: el.naturalWidth,
                        imageHeight: el.naturalHeight,
                        t: paramsRef.current.t,
                    });
                } catch {
                    json = undefined;
                }
                if (disposed) return;
            }
            suspendRef.current += 1;
            try {
                if (json && json.csScene === 2) {
                    setArtboardInternal({ w: json.artboardWidth || 1080, h: json.artboardHeight || 1080 });
                    await loadScene(json);
                } else {
                    // Legacy / agent scene, or a fresh design.
                    let bgEl: HTMLImageElement | null = null;
                    if (backgroundUrl) {
                        try { bgEl = await loadImageElement(backgroundUrl); } catch { bgEl = null; }
                    }
                    if (disposed) return;
                    const size = bgEl ? artboardFromImage(bgEl.naturalWidth, bgEl.naturalHeight) : { w: 1080, h: 1080 };
                    setArtboardInternal(size);
                    if (json) {
                        const scene = { ...json, objects: [...(json.objects || [])] };
                        const oldBg = scene.backgroundImage;
                        delete scene.backgroundImage;
                        delete scene.clipPath;
                        // The width these layers were laid out at. Agent scenes
                        // and later saves say so; the oldest saves only reveal it
                        // through how far their background was stretched.
                        let authored = Number(json.authoredWidth) || 0;
                        if (!authored && oldBg?.width && oldBg?.scaleX) {
                            authored = Math.max(1, oldBg.width * oldBg.scaleX - 12);
                        }
                        const hasFitLogo = !json.authoredWidth && scene.objects.some((o: any) => o?.fitLogo);
                        scene.background = '#FFFFFF';
                        await loadScene(scene);
                        const k = authored ? size.w / authored : 1;
                        if (Math.abs(k - 1) > 0.001) {
                            c.getObjects().forEach((o) => {
                                o.set({
                                    left: (o.left || 0) * k,
                                    top: (o.top || 0) * k,
                                    scaleX: (o.scaleX || 1) * k,
                                    scaleY: (o.scaleY || 1) * k,
                                });
                                const cp = o.clipPath as any;
                                if (cp?.absolutePositioned) {
                                    cp.set({ left: (cp.left || 0) * k, top: (cp.top || 0) * k, scaleX: (cp.scaleX || 1) * k, scaleY: (cp.scaleY || 1) * k });
                                }
                                o.setCoords();
                            });
                        }
                        if (hasFitLogo) {
                            // A plan's logo that was never rendered: place it the
                            // way the headless renderer would have.
                            c.getObjects().forEach((o: any) => {
                                const fit = o.fitLogo;
                                if (!fit || typeof o.scaleToWidth !== 'function') return;
                                const kk = size.w / (fit.frameW || size.w);
                                o.scaleToWidth(fit.width * kk);
                                const hh = o.getScaledHeight();
                                o.set({
                                    left: fit.atLeft ? fit.margin * kk : size.w - fit.margin * kk - fit.width * kk,
                                    top: fit.atTop ? fit.margin * kk : size.h - fit.margin * kk - hh,
                                });
                                o.setCoords();
                            });
                        }
                    }
                    if (backgroundUrl && bgEl) {
                        const bg = await makeImage(backgroundUrl, { x: 0, y: 0, w: size.w, h: size.h, role: 'background', name: 'Background' });
                        setLocked(bg, true);
                        decorate(bg);
                        c.insertAt(bg, 0, false);
                    }
                    if (!json && initialImageLayerUrls && initialImageLayerUrls.length > 0) {
                        // Several pictures in: start from a tidy grid, not a pile.
                        const urls = initialImageLayerUrls;
                        const cols = Math.ceil(Math.sqrt(urls.length));
                        const rows = Math.ceil(urls.length / cols);
                        const cw = size.w / cols;
                        const ch = size.h / rows;
                        for (let i = 0; i < urls.length; i++) {
                            try {
                                const img = await makeImage(urls[i], { x: (i % cols) * cw, y: Math.floor(i / cols) * ch, w: cw, h: ch });
                                decorate(img);
                                c.add(img);
                            } catch { /* skip a picture that will not load */ }
                        }
                    }
                }
                if (disposed) return;
                await loadFontsFor(c.getObjects());
                remeasureText(c);
            } finally {
                suspendRef.current -= 1;
            }
            if (disposed) return;

            // ── Events ──
            const onSel = () => bump();
            c.on('selection:created', onSel);
            c.on('selection:updated', onSel);
            c.on('selection:cleared', onSel);
            c.on('object:added', (e) => {
                if (e.target && !(e.target as any).csTemp) decorate(e.target);
                if (suspendRef.current === 0 && !(e.target as any)?.csTemp) commit();
                bump();
            });
            c.on('object:removed', (e) => {
                if (suspendRef.current === 0 && !(e.target as any)?.csTemp) commit();
                bump();
            });
            c.on('object:moving', (opt) => {
                setTransforming(true);
                // A part of a graphic drags its partners along (a dot its line, words their box).
                relinkGraphics(c, opt.target);
            });
            c.on('object:rotating', () => setTransforming(true));
            c.on('object:scaling', (opt) => {
                setTransforming(true);
                // Rectangles keep true corners at any size: bake the scale into
                // width/height every frame instead of stretching the radius.
                const r = opt.target as fabric.Rect | undefined;
                if (r?.type === 'rect' && !(r as any).csTemp) {
                    const sx = r.scaleX || 1;
                    const sy = r.scaleY || 1;
                    if (sx !== 1 || sy !== 1) {
                        const f = Math.min(sx, sy);
                        r.set({
                            width: Math.max(1, (r.width || 0) * sx),
                            height: Math.max(1, (r.height || 0) * sy),
                            rx: (r.rx || 0) * f,
                            ry: (r.ry || 0) * f,
                            scaleX: 1,
                            scaleY: 1,
                        });
                        r.setCoords();
                    }
                }
            });
            c.on('object:modified', (opt) => {
                const tgt = opt.target as any;
                // Corner-scaling a text box changes its size, not its zoom —
                // the font size the inspector shows stays the truth.
                if (tgt && isTextObject(tgt as fabric.Object) && Math.abs((tgt.scaleX || 1) - 1) > 0.001 && Math.abs((tgt.scaleX || 1) - (tgt.scaleY || 1)) < 0.001) {
                    const s = tgt.scaleX || 1;
                    tgt.set({
                        fontSize: (tgt.fontSize || 16) * s,
                        width: (tgt.width || 0) * s,
                        scaleX: 1,
                        scaleY: 1,
                        bgPadX: tgt.bgPadX ? tgt.bgPadX * s : tgt.bgPadX,
                        bgPadY: tgt.bgPadY ? tgt.bgPadY * s : tgt.bgPadY,
                        bgRadius: tgt.bgRadius ? tgt.bgRadius * s : tgt.bgRadius,
                    });
                    decorate(tgt);
                    tgt.setCoords();
                }
                if (tgt && isImageObject(tgt as fabric.Object) && tgt.csMask) refreshMask(tgt);
                if (tgt?.type === 'rect') {
                    const maxR = Math.floor(Math.min(tgt.width || 0, tgt.height || 0) / 2);
                    if ((tgt.rx || 0) > maxR) tgt.set({ rx: maxR, ry: maxR });
                }
                relinkGraphics(c, tgt);
                if (!tgt?.csTemp) commit();
                bump();
            });
            c.on('mouse:up', () => { setTransforming(false); bump(); });
            c.on('text:changed', (opt) => {
                const tb = opt.target as any;
                if (tb && (tb.bgPadX || tb.bgPadY)) {
                    // Plates hug their words; keep the plate's anchor still.
                    const before = { left: tb.left, w: tb.width };
                    fitTextWidth(tb);
                    if (tb.textAlign === 'center') tb.set({ left: before.left + (before.w - tb.width) / 2 });
                    else if (tb.textAlign === 'right') tb.set({ left: before.left + before.w - tb.width });
                } else if (tb?.csGfxId) {
                    // A graphic's words stay on one line, anchored as they were
                    // laid out, and their box and line follow as they grow.
                    // Capitals stay capitals (same length, so the caret holds).
                    if (tb.csGfxUpper && tb.text !== tb.text.toUpperCase()) tb.set({ text: tb.text.toUpperCase() });
                    const before = { left: tb.left, w: tb.width };
                    // Measure unwrapped: at its old width the new words would wrap first.
                    tb.set({ width: 100000 });
                    tb.initDimensions();
                    fitTextWidth(tb);
                    if (tb.textAlign === 'center') tb.set({ left: before.left + (before.w - tb.width) / 2 });
                    else if (tb.textAlign === 'right') tb.set({ left: before.left + before.w - tb.width });
                    tb.setCoords();
                    relinkGraphics(c, tb);
                }
                bump();
            });
            c.on('text:editing:exited', () => { commit(); bump(); });

            // Wheel: pinch / ctrl+wheel zooms at the pointer, plain wheel pans.
            c.on('mouse:wheel', (opt) => {
                const e = opt.e as WheelEvent;
                e.preventDefault();
                e.stopPropagation();
                if (e.ctrlKey || e.metaKey) {
                    const z = (c.getZoom() || 1) * Math.pow(0.996, e.deltaY);
                    zoomTo(z, { x: e.offsetX, y: e.offsetY });
                } else {
                    c.relativePan(new fabric.Point(-(e.shiftKey ? e.deltaY : e.deltaX), -(e.shiftKey ? 0 : e.deltaY)));
                    fitModeRef.current = false;
                    publishView();
                    c.requestRenderAll();
                }
            });

            // Pan: space + drag, or middle button.
            let panning = false;
            let last = { x: 0, y: 0 };
            const startPan = (opt: fabric.IEvent) => {
                const e = opt.e as MouseEvent;
                if (panning) return;
                if (spaceRef.current || e.button === 1) {
                    panning = true;
                    last = { x: e.clientX, y: e.clientY };
                    c.selection = false;
                    (c as any).skipTargetFind = true;
                }
            };
            // Left button with space held arrives before target finding; the
            // middle button only ever fires a plain mouse:down.
            c.on('mouse:down:before', startPan);
            c.on('mouse:down', (opt) => { if ((opt.e as MouseEvent).button === 1) startPan(opt); });
            c.on('mouse:move', (opt) => {
                if (!panning) return;
                const e = opt.e as MouseEvent;
                c.relativePan(new fabric.Point(e.clientX - last.x, e.clientY - last.y));
                last = { x: e.clientX, y: e.clientY };
                fitModeRef.current = false;
                publishView();
                c.requestRenderAll();
            });
            c.on('mouse:up', () => {
                if (!panning) return;
                panning = false;
                c.selection = true;
                (c as any).skipTargetFind = spaceRef.current;
            });

            // Double-click: edit text in place; recrop a picture.
            c.on('mouse:dblclick', (opt) => {
                const tgt = opt.target as any;
                if (!tgt || tgt.csTemp) return;
                if (isTextObject(tgt)) {
                    c.setActiveObject(tgt);
                    tgt.enterEditing();
                    tgt.selectAll();
                    c.requestRenderAll();
                } else if (isImageObject(tgt)) {
                    c.setActiveObject(tgt);
                    startCropRef.current?.();
                }
            });

            snapping = attachSnapping(c, () => artRef.current, () => !cropRef.current);

            ro = new ResizeObserver(() => {
                const b = paneEl.getBoundingClientRect();
                if (b.width < 50 || b.height < 50 || !canvasRef.current) return;
                c.setDimensions({ width: Math.floor(b.width), height: Math.floor(b.height) });
                if (fitModeRef.current) zoomFit();
                else { publishView(); c.requestRenderAll(); }
            });
            ro.observe(paneEl);

            applyClip();
            zoomFit();
            baseImagesRef.current = c.getObjects()
                .filter((o) => isImageObject(o) && (o as any).csRole !== 'logo')
                .map((o) => (o as any).getSrc?.() as string)
                .filter(Boolean);
            pastRef.current = [JSON.stringify(sceneJSON())];
            futureRef.current = [];
            syncCounts();
            setReady(true);
            bump();
        };
        raf = requestAnimationFrame(() => { void start(); });

        return () => {
            disposed = true;
            if (raf) cancelAnimationFrame(raf);
            snapping?.detach();
            ro?.disconnect();
            if (soonTimer.current) { clearTimeout(soonTimer.current); soonTimer.current = null; }
            canvasRef.current?.dispose();
            canvasRef.current = null;
            if ((fabric.IText.prototype as any).hiddenTextareaContainer === paneEl) {
                (fabric.IText.prototype as any).hiddenTextareaContainer = null;
            }
            pastRef.current = [];
            futureRef.current = [];
            cropRef.current = null;
            setCropping(false);
            setReady(false);
            setCounts({ past: 0, future: 0 });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initKey]);

    // ─── Space-to-pan key state ──────────────────────────────────────
    useEffect(() => {
        if (!open) return undefined;
        const inEditable = () => {
            const el = document.activeElement as HTMLElement | null;
            return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
        };
        const down = (e: KeyboardEvent) => {
            if (e.code !== 'Space' || inEditable()) return;
            const act = canvasRef.current?.getActiveObject() as any;
            if (act?.isEditing) return;
            e.preventDefault();
            if (!spaceRef.current) {
                spaceRef.current = true;
                setSpaceDown(true);
                const c = canvasRef.current as any;
                if (c) { c.skipTargetFind = true; c.defaultCursor = 'grab'; c.hoverCursor = 'grab'; }
            }
        };
        const up = (e: KeyboardEvent) => {
            if (e.code !== 'Space') return;
            spaceRef.current = false;
            setSpaceDown(false);
            const c = canvasRef.current as any;
            if (c) { c.skipTargetFind = false; c.defaultCursor = 'default'; c.hoverCursor = 'move'; }
        };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
    }, [open]);

    // ─── Helpers ─────────────────────────────────────────────────────
    const selected = (): fabric.Object[] => {
        const c = canvasRef.current;
        if (!c) return [];
        return c.getActiveObjects().filter((o) => !(o as any).csTemp);
    };

    const center = (objs: fabric.Object[]) => {
        // Drop newly made objects on the visible centre of the artboard.
        const a = artRef.current;
        if (objs.length === 1) {
            const o = objs[0];
            const w = o.getScaledWidth();
            const hh = o.getScaledHeight();
            if ((o.left ?? 0) === 0 && (o.top ?? 0) === 0) o.set({ left: (a.w - w) / 2, top: (a.h - hh) / 2 });
        }
    };

    const addObjects = useCallback((objs: fabric.Object[], opts?: { select?: boolean }) => {
        const c = canvasRef.current;
        if (!c || objs.length === 0) return;
        suspendRef.current += 1;
        objs.forEach((o) => { decorate(o); c.add(o); o.setCoords(); });
        suspendRef.current -= 1;
        if (opts?.select !== false) {
            if (objs.length === 1) c.setActiveObject(objs[0]);
            else c.setActiveObject(new fabric.ActiveSelection(objs, { canvas: c }));
        }
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const tr = () => paramsRef.current.t;

    const addTextPreset = useCallback(async (id: TextPresetId) => {
        const { brand, pair } = paramsRef.current;
        const objs = await buildTextPreset(id, { art: artRef.current, brand, pair, t: tr() });
        addObjects(objs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addObjects]);

    const addTextBox = useCallback(async () => {
        await addTextPreset('subheading');
    }, [addTextPreset]);

    const addShape = useCallback((id: ShapeId) => {
        const { brand } = paramsRef.current;
        addObjects([createShape(id, artRef.current, id === 'frame' || id === 'line' || id === 'dashed' || id === 'arrow' ? brand.ink : brand.accent)]);
    }, [addObjects]);

    const addDivider = useCallback((id: DividerId) => {
        addObjects([createDivider(id, artRef.current, paramsRef.current.brand.ink)]);
    }, [addObjects]);

    const addScrim = useCallback((id: ScrimId) => {
        const c = canvasRef.current;
        if (!c) return;
        const scrim = createScrim(id, artRef.current);
        // Scrims belong just above the background, under the type.
        suspendRef.current += 1;
        decorate(scrim);
        const bgCount = c.getObjects().filter(isBackground).length;
        c.insertAt(scrim, bgCount, false);
        suspendRef.current -= 1;
        c.setActiveObject(scrim);
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const addIcon = useCallback(async (id: string) => {
        const svg = iconSvg(id);
        if (!svg) return;
        const a = artRef.current;
        const size = Math.min(a.w, a.h) * 0.1;
        const g = await makeSvgGroup(svg, { x: (a.w - size) / 2, y: (a.h - size) / 2, size, color: paramsRef.current.brand.ink, name: id });
        addObjects([g]);
    }, [addObjects]);

    const addImage = useCallback(async (url: string, opts?: { mask?: MaskKind; asFrame?: boolean }) => {
        const a = artRef.current;
        const u = Math.min(a.w, a.h);
        const el = await loadImageElement(url);
        let box: Box;
        if (opts?.asFrame) {
            const s = u * 0.5;
            const hh = opts.mask === 'arch' ? s * 1.3 : s;
            box = { x: (a.w - s) / 2, y: (a.h - hh) / 2, w: s, h: hh };
        } else {
            const k = Math.min((a.w * 0.6) / el.naturalWidth, (a.h * 0.6) / el.naturalHeight);
            const w = el.naturalWidth * k;
            const hh = el.naturalHeight * k;
            box = { x: (a.w - w) / 2, y: (a.h - hh) / 2, w, h: hh };
        }
        const img = await makeImage(url, { ...box, mask: opts?.mask, maskRadius: u * 0.03 });
        addObjects([img]);
    }, [addObjects]);

    const addLogo = useCallback(async (url: string) => {
        const a = artRef.current;
        const u = Math.min(a.w, a.h);
        const m = u * 0.074;
        const logo = await makeLogo(url, { x: m, y: m, w: u * 0.24, h: u * 0.08 }, 'tl');
        addObjects([logo]);
    }, [addObjects]);

    // ─── Edits ───────────────────────────────────────────────────────
    const leafObjects = (objs: fabric.Object[]) => objs;

    const update = useCallback((patch: Record<string, any>, opts?: { history?: 'now' | 'soon' | 'none'; filter?: (o: fabric.Object) => boolean }) => {
        const c = canvasRef.current;
        if (!c) return;
        const objs = leafObjects(selected()).filter((o) => (opts?.filter ? opts.filter(o) : true));
        if (!objs.length) return;
        objs.forEach((o) => {
            o.set(patch);
            if (isTextObject(o) && ('bgPadX' in patch || 'bgPadY' in patch)) decorate(o);
            o.setCoords();
            (o as any).dirty = true;
        });
        c.getActiveObject()?.setCoords();
        c.requestRenderAll();
        const h = opts?.history ?? 'soon';
        if (h === 'now') commit(); else if (h === 'soon') commitSoon();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, commitSoon, bump]);

    const forEachSelected = useCallback((fn: (o: fabric.Object) => void, history: 'now' | 'soon' = 'now') => {
        const c = canvasRef.current;
        if (!c) return;
        const objs = selected();
        objs.forEach((o) => { fn(o); o.setCoords(); (o as any).dirty = true; });
        c.requestRenderAll();
        if (history === 'now') commit(); else commitSoon();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, commitSoon, bump]);

    const setFont = useCallback(async (family: string) => {
        const c = canvasRef.current;
        if (!c) return;
        const texts = selected().filter(isTextObject) as fabric.Textbox[];
        await Promise.all(texts.map((tb) => loadFont(family, tb.fontWeight as any, tb.fontStyle === 'italic')));
        texts.forEach((tb) => tb.set({ fontFamily: family }));
        remeasureText(c, family);
        commit();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    const setTextStyle = useCallback(async (patch: { fontWeight?: number; fontStyle?: string }) => {
        const c = canvasRef.current;
        if (!c) return;
        const texts = selected().filter(isTextObject) as fabric.Textbox[];
        await Promise.all(texts.map((tb) => loadFont(tb.fontFamily || 'Inter', patch.fontWeight ?? (tb.fontWeight as any), (patch.fontStyle ?? tb.fontStyle) === 'italic')));
        texts.forEach((tb) => tb.set(patch as any));
        remeasureText(c);
        commit();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    /** The one "colour" a layer has: text/shape fill, line/arrow stroke, icon paths. */
    const setColor = useCallback((color: string) => {
        forEachSelected((o) => {
            const any = o as any;
            if (o.type === 'group' && (any.csRole === 'icon' || any.csRole === 'divider')) recolorGroup(o, color);
            else if (o.type === 'line' || any.csShape === 'arrow' || any.csShape === 'frame') o.set({ stroke: color });
            else o.set({ fill: color });
            any.csColorSet = true;
            any.csColorRole = undefined;
        }, 'soon');
    }, [forEachSelected]);

    const setPlate = useCallback((on: boolean) => {
        forEachSelected((o) => {
            if (!isTextObject(o)) return;
            const any = o as any;
            const size = (o as fabric.Textbox).fontSize || 24;
            if (on) {
                o.set({ backgroundColor: any.backgroundColor || '#FFFFFF' } as any);
                any.bgPadX = any.bgPadX || size * 0.9;
                any.bgPadY = any.bgPadY || size * 0.5;
                any.bgRadius = any.bgRadius ?? size;
            } else {
                o.set({ backgroundColor: '' } as any);
                any.bgPadX = undefined;
                any.bgPadY = undefined;
                any.bgRadius = undefined;
            }
            decorate(o);
        });
    }, [forEachSelected]);

    const resetIds = (o: any) => {
        o.csId = newLayerId();
        if (typeof o.getObjects === 'function') o.getObjects().forEach(resetIds);
    };

    const deleteSelection = useCallback(() => {
        const c = canvasRef.current;
        if (!c) return;
        const objs = selected().filter((o) => !(o as any).csLocked);
        if (!objs.length) return;
        suspendRef.current += 1;
        c.discardActiveObject();
        objs.forEach((o) => c.remove(o));
        suspendRef.current -= 1;
        c.requestRenderAll();
        commit();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    const clipboardRef = useRef<fabric.Object | null>(null);

    const pasteClone = useCallback((source: fabric.Object, offset: number) => {
        const c = canvasRef.current;
        if (!c) return;
        source.clone((cloned: fabric.Object) => {
            c.discardActiveObject();
            cloned.set({ left: (cloned.left || 0) + offset, top: (cloned.top || 0) + offset, evented: true });
            suspendRef.current += 1;
            if (cloned.type === 'activeSelection') {
                (cloned as any).canvas = c;
                (cloned as fabric.ActiveSelection).forEachObject((o) => { resetIds(o); decorate(o); c.add(o); });
                cloned.setCoords();
            } else {
                resetIds(cloned);
                decorate(cloned);
                c.add(cloned);
            }
            suspendRef.current -= 1;
            c.setActiveObject(cloned);
            c.requestRenderAll();
            commit();
            bump();
        });
    }, [commit, bump]);

    const duplicate = useCallback(() => {
        const act = canvasRef.current?.getActiveObject();
        if (!act || (act as any).csTemp) return;
        pasteClone(act, Math.min(artRef.current.w, artRef.current.h) * 0.02);
    }, [pasteClone]);

    const copy = useCallback(() => {
        const act = canvasRef.current?.getActiveObject();
        if (!act || (act as any).csTemp) return;
        act.clone((cl: fabric.Object) => { clipboardRef.current = cl; });
    }, []);

    const paste = useCallback(() => {
        const src = clipboardRef.current;
        if (!src) return;
        const off = Math.min(artRef.current.w, artRef.current.h) * 0.02;
        pasteClone(src, off);
        src.set({ left: (src.left || 0) + off, top: (src.top || 0) + off });
    }, [pasteClone]);

    const group = useCallback(() => {
        const c = canvasRef.current;
        const act = c?.getActiveObject();
        if (!c || act?.type !== 'activeSelection') return;
        suspendRef.current += 1;
        const g = (act as any).toGroup() as fabric.Group;
        (g as any).csId = newLayerId();
        (g as any).csName = 'Group';
        decorate(g);
        suspendRef.current -= 1;
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const ungroup = useCallback(() => {
        const c = canvasRef.current;
        const act = c?.getActiveObject();
        if (!c || act?.type !== 'group') return;
        suspendRef.current += 1;
        const sel = (act as any).toActiveSelection() as fabric.ActiveSelection;
        sel.getObjects().forEach(decorate);
        suspendRef.current -= 1;
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const select = useCallback((objs: fabric.Object[] | fabric.Object | null) => {
        const c = canvasRef.current;
        if (!c) return;
        c.discardActiveObject();
        const list = (Array.isArray(objs) ? objs : objs ? [objs] : []).filter((o) => o.visible !== false);
        if (list.length === 1) c.setActiveObject(list[0]);
        else if (list.length > 1) c.setActiveObject(new fabric.ActiveSelection(list, { canvas: c }));
        c.requestRenderAll();
        bump();
    }, [bump]);

    const selectAll = useCallback(() => {
        const c = canvasRef.current;
        if (!c) return;
        select(c.getObjects().filter((o) => !(o as any).csLocked && !(o as any).csTemp && o.visible !== false));
    }, [select]);

    /** Work on objects with absolute coordinates, then put the selection back. */
    const withLooseSelection = (fn: (objs: fabric.Object[]) => void) => {
        const c = canvasRef.current;
        if (!c) return;
        const objs = selected();
        if (!objs.length) return;
        const wasMulti = objs.length > 1;
        c.discardActiveObject();
        fn(objs);
        objs.forEach((o) => o.setCoords());
        if (wasMulti) c.setActiveObject(new fabric.ActiveSelection(objs, { canvas: c }));
        else c.setActiveObject(objs[0]);
        c.requestRenderAll();
        commit();
        bump();
    };

    const align = useCallback((mode: 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom') => {
        withLooseSelection((objs) => {
            const rects = objs.map((o) => o.getBoundingRect(true, true));
            let ref: { l: number; t: number; r: number; b: number };
            if (objs.length > 1) {
                ref = {
                    l: Math.min(...rects.map((r) => r.left)),
                    t: Math.min(...rects.map((r) => r.top)),
                    r: Math.max(...rects.map((r) => r.left + r.width)),
                    b: Math.max(...rects.map((r) => r.top + r.height)),
                };
            } else {
                ref = { l: 0, t: 0, r: artRef.current.w, b: artRef.current.h };
            }
            objs.forEach((o, i) => {
                const r = rects[i];
                if (mode === 'left') o.set({ left: (o.left || 0) + (ref.l - r.left) });
                if (mode === 'right') o.set({ left: (o.left || 0) + (ref.r - (r.left + r.width)) });
                if (mode === 'hcenter') o.set({ left: (o.left || 0) + ((ref.l + ref.r) / 2 - (r.left + r.width / 2)) });
                if (mode === 'top') o.set({ top: (o.top || 0) + (ref.t - r.top) });
                if (mode === 'bottom') o.set({ top: (o.top || 0) + (ref.b - (r.top + r.height)) });
                if (mode === 'vcenter') o.set({ top: (o.top || 0) + ((ref.t + ref.b) / 2 - (r.top + r.height / 2)) });
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    const distribute = useCallback((axis: 'h' | 'v') => {
        withLooseSelection((objs) => {
            if (objs.length < 3) return;
            const items = objs.map((o) => ({ o, r: o.getBoundingRect(true, true) }));
            const key = axis === 'h' ? 'left' : 'top';
            const size = axis === 'h' ? 'width' : 'height';
            items.sort((a, b) => a.r[key] - b.r[key]);
            const start = items[0].r[key];
            const end = Math.max(...items.map((i) => i.r[key] + i.r[size]));
            const total = items.reduce((acc, i) => acc + i.r[size], 0);
            const gap = (end - start - total) / (items.length - 1);
            let cursor = start;
            items.forEach((i) => {
                const delta = cursor - i.r[key];
                i.o.set({ [key]: ((i.o as any)[key] || 0) + delta } as any);
                cursor += i.r[size] + gap;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    const floor = () => (canvasRef.current?.getObjects() || []).filter(isBackground).length;

    const order = useCallback((dir: 'front' | 'forward' | 'backward' | 'back') => {
        const c = canvasRef.current;
        if (!c) return;
        const objs = selected();
        if (!objs.length) return;
        const min = floor();
        const list = dir === 'front' || dir === 'backward' ? objs : [...objs].reverse();
        list.forEach((o) => {
            const idx = c.getObjects().indexOf(o);
            if (dir === 'front') c.bringToFront(o);
            else if (dir === 'forward') c.bringForward(o);
            else if (dir === 'back') c.moveTo(o, isBackground(o) ? 0 : min);
            else if (idx - 1 >= (isBackground(o) ? 0 : min)) c.moveTo(o, idx - 1);
        });
        c.requestRenderAll();
        commit();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    const reorder = useCallback((obj: fabric.Object, toIndex: number) => {
        const c = canvasRef.current;
        if (!c) return;
        c.moveTo(obj, Math.max(0, Math.min(c.getObjects().length - 1, toIndex)));
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const toggleLock = useCallback((obj?: fabric.Object) => {
        const c = canvasRef.current;
        if (!c) return;
        const targets = obj ? [obj] : selected();
        if (!targets.length) return;
        const lock = !(targets[0] as any).csLocked;
        if (lock) c.discardActiveObject();
        targets.forEach((o) => { setLocked(o, lock); decorate(o); });
        c.requestRenderAll();
        commit();
        bump();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commit, bump]);

    const toggleVisible = useCallback((obj: fabric.Object) => {
        const c = canvasRef.current;
        if (!c) return;
        obj.set({ visible: obj.visible === false });
        if (obj.visible === false && c.getActiveObjects().includes(obj)) c.discardActiveObject();
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const rename = useCallback((obj: fabric.Object, name: string) => {
        (obj as any).csName = name.trim() || undefined;
        (obj as any).csNameUser = name.trim() ? true : undefined;
        commit();
        bump();
    }, [commit, bump]);

    const replaceObject = useCallback((prev: fabric.Object, next: fabric.Object) => {
        const c = canvasRef.current;
        if (!c) return;
        const idx = c.getObjects().indexOf(prev);
        if (idx < 0) return;
        suspendRef.current += 1;
        c.discardActiveObject();
        c.remove(prev);
        decorate(next);
        c.insertAt(next, idx, false);
        suspendRef.current -= 1;
        c.setActiveObject(next);
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const nudge = useCallback((dx: number, dy: number) => {
        const c = canvasRef.current;
        const act = c?.getActiveObject();
        if (!c || !act || (act as any).lockMovementX) return;
        act.set({ left: (act.left || 0) + dx, top: (act.top || 0) + dy });
        act.setCoords();
        c.requestRenderAll();
        commitSoon();
        bump();
    }, [commitSoon, bump]);

    const flip = useCallback((axis: 'h' | 'v') => {
        forEachSelected((o) => {
            if (axis === 'h') o.set({ flipX: !o.flipX });
            else o.set({ flipY: !o.flipY });
        });
    }, [forEachSelected]);

    const setGeometry = useCallback((patch: { x?: number; y?: number; w?: number; h?: number; angle?: number }) => {
        const c = canvasRef.current;
        const o = c?.getActiveObject();
        if (!c || !o) return;
        if (patch.angle !== undefined) o.rotate(patch.angle);
        if (patch.w !== undefined && patch.w > 0) {
            if (isTextObject(o)) o.set({ width: patch.w / (o.scaleX || 1) });
            else if (o.type === 'rect') o.set({ width: patch.w / (o.scaleX || 1) });
            else {
                const k = patch.w / (o.getScaledWidth() || 1);
                o.set({ scaleX: (o.scaleX || 1) * k });
                if (o.type === 'image' || o.type === 'group') o.set({ scaleY: (o.scaleY || 1) * k });
            }
        }
        if (patch.h !== undefined && patch.h > 0 && !isTextObject(o)) {
            if (o.type === 'rect') o.set({ height: patch.h / (o.scaleY || 1) });
            else {
                const k = patch.h / (o.getScaledHeight() || 1);
                o.set({ scaleY: (o.scaleY || 1) * k });
                if (o.type === 'image' || o.type === 'group') o.set({ scaleX: (o.scaleX || 1) * k });
            }
        }
        if (patch.x !== undefined || patch.y !== undefined) {
            o.setCoords();
            const r = o.getBoundingRect(true, true);
            if (patch.x !== undefined) o.set({ left: (o.left || 0) + (patch.x - r.left) });
            if (patch.y !== undefined) o.set({ top: (o.top || 0) + (patch.y - r.top) });
        }
        if (isImageObject(o)) refreshMask(o);
        o.setCoords();
        c.requestRenderAll();
        commitSoon();
        bump();
    }, [commitSoon, bump]);

    // ─── Images ──────────────────────────────────────────────────────
    const replaceImage = useCallback(async (url: string, target?: fabric.Image) => {
        const c = canvasRef.current;
        if (!c) return;
        const old = target || (c.getActiveObject() as fabric.Image | null);
        if (!old || !isImageObject(old)) return;
        const el = await loadImageElement(url);
        const box = { x: old.left || 0, y: old.top || 0, w: old.getScaledWidth(), h: old.getScaledHeight() };
        const angle = old.angle || 0;
        old.set({ angle: 0 });
        old.set({ filters: [] } as any);
        old.setElement(el);
        old.set({ crossOrigin: el.crossOrigin ? 'anonymous' : null } as any);
        coverFit(old, box);
        old.rotate(angle);
        old.setCoords();
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const setMask = useCallback((kind: MaskKind, radius?: number) => {
        forEachSelected((o) => {
            if (!isImageObject(o)) return;
            if (kind === 'circle') {
                // A circle frame wants a square picture: crop to the short side.
                const w = o.getScaledWidth();
                const hh = o.getScaledHeight();
                if (Math.abs(w - hh) > 1) {
                    const s = Math.min(w, hh);
                    coverFit(o, { x: (o.left || 0) + (w - s) / 2, y: (o.top || 0) + (hh - s) / 2, w: s, h: s });
                }
            }
            applyMask(o, kind, radius ?? (o as any).csMaskRadius ?? Math.min(artRef.current.w, artRef.current.h) * 0.03);
        }, radius !== undefined ? 'soon' : 'now');
    }, [forEachSelected]);

    const fitToArtboard = useCallback((target?: fabric.Image, mode: 'cover' | 'contain' = 'cover') => {
        const c = canvasRef.current;
        if (!c) return;
        const img = target || (c.getActiveObject() as fabric.Image | null);
        if (!img || !isImageObject(img)) return;
        const a = artRef.current;
        img.set({ angle: 0 });
        if (mode === 'cover') coverFit(img, { x: 0, y: 0, w: a.w, h: a.h });
        else containFit(img, { x: 0, y: 0, w: a.w, h: a.h });
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const setAsBackground = useCallback((target?: fabric.Image) => {
        const c = canvasRef.current;
        if (!c) return;
        const img = target || (c.getActiveObject() as fabric.Image | null);
        if (!img || !isImageObject(img)) return;
        suspendRef.current += 1;
        c.discardActiveObject();
        // One background at a time: the previous one becomes a normal layer.
        c.getObjects().filter(isBackground).forEach((o) => {
            if (o === img) return;
            c.remove(o);
        });
        const a = artRef.current;
        applyMask(img, 'none');
        coverFit(img, { x: 0, y: 0, w: a.w, h: a.h });
        (img as any).csRole = 'background';
        (img as any).csName = 'Background';
        setLocked(img, true);
        decorate(img);
        c.moveTo(img, 0);
        suspendRef.current -= 1;
        c.requestRenderAll();
        commit();
        bump();
    }, [commit, bump]);

    const setBackgroundFromUrl = useCallback(async (url: string) => {
        const c = canvasRef.current;
        if (!c) return;
        const a = artRef.current;
        const existing = c.getObjects().find((o) => isBackground(o) && isImageObject(o)) as fabric.Image | undefined;
        if (existing) {
            await replaceImage(url, existing);
            coverFit(existing, { x: 0, y: 0, w: a.w, h: a.h });
            c.requestRenderAll();
            commit();
            return;
        }
        const img = await makeImage(url, { x: 0, y: 0, w: a.w, h: a.h, role: 'background', name: 'Background' });
        setLocked(img, true);
        suspendRef.current += 1;
        decorate(img);
        c.insertAt(img, 0, false);
        suspendRef.current -= 1;
        c.requestRenderAll();
        commit();
        bump();
    }, [replaceImage, commit, bump]);

    const filterState = (img: fabric.Image): FilterState => {
        const s: FilterState = { brightness: 0, contrast: 0, saturation: 0, blur: 0, grayscale: false };
        (img.filters || []).forEach((f: any) => {
            if (!f) return;
            if (f.type === 'Brightness') s.brightness = f.brightness;
            if (f.type === 'Contrast') s.contrast = f.contrast;
            if (f.type === 'Saturation') s.saturation = f.saturation;
            if (f.type === 'Blur') s.blur = f.blur;
            if (f.type === 'Grayscale') s.grayscale = true;
        });
        return s;
    };

    const applyFilters = useCallback((patch: Partial<FilterState>) => {
        forEachSelected((o) => {
            if (!isImageObject(o)) return;
            const s = { ...filterState(o), ...patch };
            const F: any = fabric.Image.filters;
            const list: any[] = [];
            if (s.brightness) list.push(new F.Brightness({ brightness: s.brightness }));
            if (s.contrast) list.push(new F.Contrast({ contrast: s.contrast }));
            if (s.saturation) list.push(new F.Saturation({ saturation: s.saturation }));
            if (s.blur > 0) list.push(new F.Blur({ blur: s.blur }));
            if (s.grayscale) list.push(new F.Grayscale());
            o.filters = list;
            o.applyFilters();
        }, 'soon');
    }, [forEachSelected]);

    // ─── Crop mode ───────────────────────────────────────────────────
    //
    // The full picture appears ghosted behind a crop frame. Drag/resize the
    // frame to choose what is kept; drag the ghost to slide the picture under
    // it. Apply writes Fabric's own cropX/cropY/width/height.
    const cropRef = useRef<{
        img: fabric.Image;
        ghost: fabric.Image;
        frame: fabric.Rect;
        prev: Record<string, any>;
        aspect: number | null;
    } | null>(null);
    const startCropRef = useRef<(() => void) | null>(null);

    const startCrop = useCallback(() => {
        const c = canvasRef.current;
        const img = c?.getActiveObject();
        if (!c || !img || !isImageObject(img) || cropRef.current || (img as any).csLocked) return;
        if (Math.abs(img.angle || 0) > 0.01) return;
        suspendRef.current += 1;
        const s = img.scaleX || 1;
        const { w: nw, h: nh } = naturalSize(img);
        const el = (img as any)._originalElement || img.getElement();
        const ghost = new fabric.Image(el, {
            left: (img.left || 0) - (img.cropX || 0) * s,
            top: (img.top || 0) - (img.cropY || 0) * s,
            scaleX: s,
            scaleY: s,
            width: nw,
            height: nh,
            opacity: 0.35,
            originX: 'left',
            originY: 'top',
            excludeFromExport: true,
            flipX: img.flipX,
            flipY: img.flipY,
        } as any);
        (ghost as any).csTemp = true;
        ghost.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false, mtr: false });
        ghost.set({ borderColor: '#FFFFFF', cornerColor: '#FFFFFF', cornerStrokeColor: '#1A1917', transparentCorners: false, cornerStyle: 'circle' } as any);
        const frame = new fabric.Rect({
            left: img.left,
            top: img.top,
            width: img.getScaledWidth(),
            height: img.getScaledHeight(),
            fill: 'rgba(0,0,0,0)',
            stroke: '#FFFFFF',
            strokeWidth: 2,
            strokeDashArray: [8, 6],
            strokeUniform: true,
            originX: 'left',
            originY: 'top',
            excludeFromExport: true,
            cornerColor: '#FFFFFF',
            cornerStrokeColor: '#1A1917',
            cornerStyle: 'circle',
            transparentCorners: false,
            borderColor: '#FFFFFF',
            lockRotation: true,
        } as any);
        (frame as any).csTemp = true;
        frame.setControlsVisibility({ mtr: false });
        const idx = c.getObjects().indexOf(img);
        const prev = { visible: img.visible };
        // The real image shows the kept area at full strength while cropping.
        c.insertAt(ghost, idx, false);
        c.add(frame);
        const syncPreview = () => {
            const st = ghost.scaleX || 1;
            const fx = frame.left || 0;
            const fy = frame.top || 0;
            const fw = frame.getScaledWidth();
            const fh = frame.getScaledHeight();
            img.set({
                left: fx,
                top: fy,
                scaleX: st,
                scaleY: st,
                cropX: Math.max(0, (fx - (ghost.left || 0)) / st),
                cropY: Math.max(0, (fy - (ghost.top || 0)) / st),
                width: Math.max(1, fw / st),
                height: Math.max(1, fh / st),
            } as any);
            img.setCoords();
        };
        (frame as any).__sync = syncPreview;
        frame.on('moving', syncPreview);
        frame.on('scaling', () => {
            const a = cropRef.current?.aspect;
            if (a) frame.set({ scaleY: (frame.getScaledWidth() / a) / (frame.height || 1) });
            syncPreview();
        });
        ghost.on('moving', syncPreview);
        ghost.on('scaling', syncPreview);
        img.set({ selectable: false, evented: false, clipPath: undefined } as any);
        cropRef.current = { img, ghost, frame, prev, aspect: null };
        c.setActiveObject(frame);
        suspendRef.current -= 1;
        setCropping(true);
        c.requestRenderAll();
        bump();
    }, [bump]);
    startCropRef.current = startCrop;

    const endCrop = (apply: boolean) => {
        const c = canvasRef.current;
        const st = cropRef.current;
        if (!c || !st) return;
        suspendRef.current += 1;
        const { img, ghost, frame } = st;
        if (apply) {
            // Clamp the frame inside the picture, then commit the crop.
            const gs = ghost.scaleX || 1;
            const gl = ghost.left || 0;
            const gt = ghost.top || 0;
            const gw = ghost.getScaledWidth();
            const gh = ghost.getScaledHeight();
            let fx = Math.max(gl, frame.left || 0);
            let fy = Math.max(gt, frame.top || 0);
            const fw = Math.min(frame.getScaledWidth(), gl + gw - fx);
            const fh = Math.min(frame.getScaledHeight(), gt + gh - fy);
            fx = Math.min(fx, gl + gw - fw);
            fy = Math.min(fy, gt + gh - fh);
            img.set({
                left: fx,
                top: fy,
                scaleX: gs,
                scaleY: gs,
                cropX: (fx - gl) / gs,
                cropY: (fy - gt) / gs,
                width: Math.max(1, fw / gs),
                height: Math.max(1, fh / gs),
            } as any);
        } else {
            img.set((st as any).saved || {});
        }
        img.set({ selectable: true, evented: true } as any);
        refreshMask(img);
        img.setCoords();
        c.remove(ghost);
        c.remove(frame);
        cropRef.current = null;
        suspendRef.current -= 1;
        setCropping(false);
        c.setActiveObject(img);
        c.requestRenderAll();
        if (apply) commit();
        bump();
    };

    // Snapshot of the image before cropping, so Cancel can restore it.
    const startCropWithSave = useCallback(() => {
        const c = canvasRef.current;
        const img = c?.getActiveObject();
        if (!img || !isImageObject(img)) return;
        const saved = {
            left: img.left, top: img.top, scaleX: img.scaleX, scaleY: img.scaleY,
            cropX: img.cropX, cropY: img.cropY, width: img.width, height: img.height,
        };
        startCrop();
        if (cropRef.current) (cropRef.current as any).saved = saved;
    }, [startCrop]);
    startCropRef.current = startCropWithSave;

    const applyCrop = useCallback(() => endCrop(true), // eslint-disable-next-line react-hooks/exhaustive-deps
        [commit, bump]);
    const cancelCrop = useCallback(() => endCrop(false), // eslint-disable-next-line react-hooks/exhaustive-deps
        [commit, bump]);

    const setCropAspect = useCallback((ratio: number | null) => {
        const c = canvasRef.current;
        const st = cropRef.current;
        if (!c || !st) return;
        st.aspect = ratio;
        if (ratio) {
            const { ghost, frame } = st;
            const gw = ghost.getScaledWidth();
            const gh = ghost.getScaledHeight();
            let w = frame.getScaledWidth();
            let hh = w / ratio;
            if (hh > gh) { hh = gh; w = hh * ratio; }
            if (w > gw) { w = gw; hh = w / ratio; }
            const cx = (frame.left || 0) + frame.getScaledWidth() / 2;
            const cy = (frame.top || 0) + frame.getScaledHeight() / 2;
            frame.set({ width: w, height: hh, scaleX: 1, scaleY: 1, left: cx - w / 2, top: cy - hh / 2 });
            frame.setCoords();
            (frame as any).__sync?.();
        }
        c.requestRenderAll();
        bump();
    }, [bump]);

    // ─── Artboard ────────────────────────────────────────────────────
    const setArtboard = useCallback((size: ArtboardSize) => {
        const c = canvasRef.current;
        if (!c) return;
        const prev = artRef.current;
        const next = { w: Math.round(size.w), h: Math.round(size.h) };
        if (prev.w === next.w && prev.h === next.h) return;
        // Keep the composition centred: shift every layer by half the change,
        // then re-cover the background so the picture still fills the frame.
        const dx = (next.w - prev.w) / 2;
        const dy = (next.h - prev.h) / 2;
        c.discardActiveObject();
        c.getObjects().forEach((o) => {
            if (isBackground(o)) return;
            o.set({ left: (o.left || 0) + dx, top: (o.top || 0) + dy });
            o.setCoords();
        });
        setArtboardInternal(next);
        fitBackgroundLayers();
        zoomFit();
        commit();
        bump();
    }, [setArtboardInternal, fitBackgroundLayers, zoomFit, commit, bump]);

    const setArtboardColor = useCallback((color: string) => {
        const c = canvasRef.current;
        if (!c) return;
        c.backgroundColor = color;
        c.requestRenderAll();
        commitSoon();
        bump();
    }, [commitSoon, bump]);

    // ─── Templates & brand ───────────────────────────────────────────
    const templateImages = useCallback((): string[] => {
        const c = canvasRef.current;
        const urls: string[] = [];
        const push = (u?: string | null) => { if (u && !urls.includes(u)) urls.push(u); };
        push(paramsRef.current.backgroundUrl);
        // Stable order: what the design opened with, then what was added since,
        // then the project's other pictures — so re-applying templates never
        // shuffles which picture is the hero.
        baseImagesRef.current.forEach(push);
        (c?.getObjects() || []).forEach((o) => {
            if (!isImageObject(o) || (o as any).csRole === 'logo' || (o as any).csTemp) return;
            push((o as any).getSrc?.());
        });
        paramsRef.current.projectImages.forEach(push);
        return urls.slice(0, 6);
    }, []);

    const applyTemplate = useCallback(async (id: string, opts?: { pair?: FontPair; useBrandFont?: boolean }) => {
        const c = canvasRef.current;
        const def = findTemplate(id);
        if (!c || !def) return;
        const { brand, pair, t } = paramsRef.current;
        const res = await def.build({
            art: artRef.current,
            brand,
            pair: opts?.pair || pair,
            t,
            images: templateImages(),
            useBrandFont: opts?.useBrandFont ?? true,
        });
        await batch(() => {
            c.discardActiveObject();
            c.getObjects().slice().forEach((o) => c.remove(o));
            c.backgroundColor = res.background;
            res.objects.forEach((o) => { decorate(o); c.add(o); });
            c.requestRenderAll();
        });
    }, [templateImages, batch]);

    const applyBrand = useCallback(async () => {
        const c = canvasRef.current;
        if (!c) return;
        const { brand } = paramsRef.current;
        const head = brand.font;
        if (head) await Promise.all([loadFont(head, 400), loadFont(head, 400, true), loadFont(head, 500)]);
        await batch(() => {
            const walk = (o: any) => {
                if (typeof o.getObjects === 'function' && o.csRole !== 'icon') o.getObjects().forEach(walk);
                const role = o.csColorRole;
                if (role) {
                    const col = colorForRole(brand, role);
                    if (o.csRole === 'icon' || (o.type === 'group')) recolorGroup(o, col);
                    else if (o.type === 'line' || o.csShape === 'arrow' || o.csShape === 'frame') o.set({ stroke: col });
                    else o.set({ fill: col });
                }
                if (isTextObject(o as fabric.Object) && o.backgroundColor && (o.bgPadX || o.bgPadY) && role === 'onAccent') {
                    o.set({ backgroundColor: brand.accent });
                }
                if (head && isTextObject(o as fabric.Object) && o.csFontRole === 'heading') o.set({ fontFamily: head });
            };
            c.getObjects().forEach(walk);
            remeasureText(c);
            c.requestRenderAll();
        });
    }, [batch]);

    // ─── Output ──────────────────────────────────────────────────────
    const exportBlob = useCallback(async (type: 'image/png' | 'image/jpeg' = 'image/png', quality = 0.92): Promise<Blob> => {
        const c = canvasRef.current;
        if (!c) throw new Error('No canvas');
        if (cropRef.current) endCrop(true);
        const a = artRef.current;
        const active = c.getActiveObject();
        const editing = (active as any)?.isEditing;
        if (editing) (active as any).exitEditing();
        const savedVpt = (c.viewportTransform || [1, 0, 0, 1, 0, 0]).slice();
        c.viewportTransform = [1, 0, 0, 1, 0, 0] as any;
        let el: HTMLCanvasElement;
        try {
            el = (c as any).toCanvasElement(1, {
                left: 0,
                top: 0,
                width: a.w,
                height: a.h,
                filter: (o: any) => !o.csTemp,
            });
        } finally {
            c.viewportTransform = savedVpt as any;
            c.requestRenderAll();
        }
        let out = el;
        if (type === 'image/jpeg') {
            // JPEG has no alpha: flatten onto the artboard colour (or white).
            const flat = document.createElement('canvas');
            flat.width = el.width;
            flat.height = el.height;
            const ctx = flat.getContext('2d')!;
            ctx.fillStyle = typeof c.backgroundColor === 'string' && c.backgroundColor ? c.backgroundColor : '#FFFFFF';
            ctx.fillRect(0, 0, flat.width, flat.height);
            ctx.drawImage(el, 0, 0);
            out = flat;
        }
        return new Promise<Blob>((resolve, reject) => {
            out.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not read the design'))), type, quality);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = useCallback(() => {
        canvasRef.current?.requestRenderAll();
        bump();
    }, [bump]);

    const c = canvasRef.current;
    const activeObj = c?.getActiveObject() || null;
    const selection = c ? c.getActiveObjects().filter((o) => !(o as any).csTemp) : [];

    return useMemo<DesignEditorApi>(() => ({
        ready,
        canvas: canvasRef.current,
        artboard,
        view,
        version,
        canUndo: counts.past > 1,
        canRedo: counts.future > 0,
        cropping,
        transforming,
        spaceDown,
        selection,
        active: activeObj,
        undo, redo, commit, commitSoon, refresh,
        addObjects, addTextPreset, addTextBox, addShape, addDivider, addScrim, addIcon, addImage, addLogo,
        update, forEachSelected, setFont, setTextStyle, setColor, setPlate,
        deleteSelection, duplicate, copy, paste, group, ungroup, selectAll, select,
        align, distribute, order, reorder, toggleLock, toggleVisible, rename, nudge, replaceObject, flip, setGeometry,
        replaceImage, setMask, setAsBackground, setBackgroundFromUrl, fitToArtboard, applyFilters,
        startCrop: startCropWithSave, applyCrop, cancelCrop, setCropAspect,
        setArtboard, setArtboardColor, zoomTo: (z: number) => zoomTo(z), zoomBy, zoomFit,
        applyTemplate, applyBrand, templateImages,
        sceneJSON, exportBlob,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [ready, artboard, view, version, counts, cropping, transforming, spaceDown]);
}

/** Read the filter sliders' state off an image. */
export function readFilters(img: fabric.Image): FilterState {
    const s: FilterState = { brightness: 0, contrast: 0, saturation: 0, blur: 0, grayscale: false };
    (img.filters || []).forEach((f: any) => {
        if (!f) return;
        if (f.type === 'Brightness') s.brightness = f.brightness;
        if (f.type === 'Contrast') s.contrast = f.contrast;
        if (f.type === 'Saturation') s.saturation = f.saturation;
        if (f.type === 'Blur') s.blur = f.blur;
        if (f.type === 'Grayscale') s.grayscale = true;
    });
    return s;
}

export { groupColor };
